import { memo, useEffect, useReducer, useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Text } from '@Sage/hoi-poi-ui';
import { v4 as uuid } from 'uuid';
import { getAutomation, putAutomation, createAutomation } from 'services/AutomationsService';

import { successToast, errorToast } from 'utils/toast';
import { getLiteral } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import dot from 'utils/dot';
import ConfirmModal from 'components/ConfirmModal';
import { isEmptyObject } from 'utils/objects';
import { publish } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';
import { logEvent } from 'utils/tracking';
import {
    initBoxCount,
    getNextBoxCount,
    CONDITION_TYPE_NO_INPUT,
    ACTION_OPERATORS_NO_INPUT,
} from 'containers/settings/AutomationSettings/utils';

import AutomationBuilderHeader from './AutomationBuilderHeader';
import AutomationBuilderCanvas from './AutomationBuilderCanvas';
import AutomationBuilderFooter from './AutomationBuilderFooter';

import AutomationBuilderHeaderModalForm from './components/AutomationBuilderHeaderModalForm';
import AutomationBuilderWarningModal from './components/AutomationBuilderWarningModal';

import './styles.scss';

const automationRedirectPath = () => {
    const currentPath = window.location.href;

    if (!currentPath.includes('/settings/automations')) return;

    const hashIndex = currentPath.indexOf('#');
    if (hashIndex !== -1) {
        const hashPath = currentPath.substring(hashIndex + 1);
        const pathSegments = hashPath.split('/').filter((segment) => segment);
        const automationsIndex = pathSegments.indexOf('automations');

        if (automationsIndex !== -1) {
            const nextSegment = pathSegments[automationsIndex + 1];

            // Only redirect to /automations if the next segment is a number (automation ID)
            if (nextSegment && !isNaN(nextSegment) && !isNaN(parseFloat(nextSegment))) {
                ensureRoute('/settings/automations');
            }
        }
    }
};

const initialState = {
    id: null,
    data: null,
    open: false,
    loading: true,
    error: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'load':
            return {
                ...state,
                loading: false,
                error: false,
                open: true,
                data: action.data,
                id: action.id,
            };
        case 'changeData':
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data,
                },
            };
        case 'changeField':
            return {
                ...state,
                data: dot.set(state.data, action.field, action.value),
            };
        case 'loading':
            return {
                ...state,
                loading: true,
                error: false,
                data: null,
            };
        case 'error':
            return {
                ...state,
                data: null,
                loading: false,
                error: true,
            };
        case 'close':
            return {
                ...state,
                open: false,
            };
        default:
            throw new Error('No action provided');
    }
}

const AutomationBuilder = memo(({ onRef }) => {
    const modalRef = useRef();
    const warningModalRef = useRef();
    const [state, dispatch] = useReducer(reducer, initialState);
    const [isTouched, setIsTouched] = useState(false);
    const [discardOpen, setDiscardOpen] = useState(false);
    const wasEnabledRef = useRef(false);

    useEffect(() => {
        onRef &&
            onRef({
                open(id, options) {
                    setIsTouched(false);
                    initBoxCount();

                    const count = {
                        conditions: 0,
                        action: 0,
                    };
                    // Add IDs to nodes that don't have them (retrocompatibility)
                    const addIdsToNodes = (node) => {
                        if (!node.id) {
                            node.id = uuid();
                        }
                        if (node.next && Array.isArray(node.next)) {
                            node.next.forEach((nextNode) => {
                                if (nextNode.position) {
                                    count[nextNode.type] = nextNode.position;
                                } else {
                                    count[nextNode.type] = getNextBoxCount(nextNode.type);
                                }
                                nextNode.position = count[nextNode.type];
                                addIdsToNodes(nextNode);
                            });
                        }
                    };

                    if (id) {
                        logEvent({
                            event: 'settings',
                            submodule: 'automations',
                            functionality: 'updateStart',
                        });
                        getAutomation(id)
                            .then((data) => {
                                if (data.scene) {
                                    addIdsToNodes(data.scene);
                                    initBoxCount(count.conditions, count.action);
                                }
                                wasEnabledRef.current = data.enabled;

                                if (options?.duplicate) {
                                    dispatch({
                                        type: 'load',
                                        data: {
                                            ...data,
                                            name: '',
                                            description: '',
                                        },
                                    });
                                } else {
                                    dispatch({ type: 'load', data, id });
                                }
                            })
                            .catch((e) => {
                                console.error(e);
                                errorToast({ text: getLiteral('error_generalerror') });
                            });
                    } else {
                        // Default hardcoded scene
                        logEvent({
                            event: 'settings',
                            submodule: 'automations',
                            functionality: 'createStart',
                        });

                        if (options?.scene) {
                            addIdsToNodes(options?.scene);
                            initBoxCount(count.conditions, count.action);
                        }

                        dispatch({
                            type: 'load',
                            data: {
                                type: 'trigger',
                                enabled: true,
                                parameters: {},
                                isFromTemplate: !!options?.scene,
                                defaultEntity: options?.defaultEntity,
                                scene: options?.scene || {
                                    id: uuid(),
                                    type: 'trigger',
                                    name: 'entity_crud',
                                    parameters: {},
                                    next: [
                                        {
                                            id: uuid(),
                                            type: 'conditions',
                                            name: 'conditions_logic',
                                            position: getNextBoxCount('conditions'),
                                            parameters: {},
                                            next: [
                                                {
                                                    id: uuid(),
                                                    type: 'action',
                                                    name: 'update_entity_related',
                                                    position: getNextBoxCount('action'),
                                                    parameters: {},
                                                },
                                            ],
                                        },
                                    ],
                                },
                            },
                            id: null,
                        });
                    }
                },
            });
    }, [onRef]);

    const onRequestClose = useCallback(() => {
        if (isTouched) {
            setDiscardOpen(true);
        } else {
            dispatch({ type: 'close' });
            automationRedirectPath();
        }
    }, [isTouched]);

    const onChange = useCallback((field, value) => {
        dispatch({ type: 'changeField', field, value });
        setIsTouched(true);
    }, []);

    const onSave = useCallback(() => {
        function save(detailsData = {}) {
            const finalData = {
                ...state.data,
                ...detailsData,
            };
            let call;
            if (state.id) {
                call = () => putAutomation(state.id, finalData);
            } else {
                call = () => createAutomation(finalData);
            }

            call()
                .then(() => {
                    publish(`${REFRESH_TABLE}_automations`);
                    dispatch({ type: 'close' });
                    ensureRoute('/settings/automations');

                    let toastLiteral = 'succes_entitycreatedsuccessfully';
                    if (state.id) {
                        logEvent({
                            event: 'settings',
                            submodule: 'automations',
                            functionality: 'updateFinish',
                        });
                        toastLiteral = 'succes_entityupdatedsuccessfully';
                    } else {
                        if (state.data?.isFromTemplate) {
                            logEvent({
                                event: state.data?.defaultEntity || 'settings',
                                submodule: 'automationsTemplate',
                                functionality: 'createTemplateFinish',
                            });
                        } else {
                            logEvent({
                                event: 'settings',
                                submodule: 'automations',
                                functionality: 'createFinish',
                            });
                        }
                    }

                    successToast({
                        text: getLiteral(toastLiteral),
                    });
                })
                .catch((e) => {
                    console.error(e);
                    errorToast({ text: getLiteral('error_generalerror') });
                });
        }

        let foundIncomplete = false;

        // Check all nodes are complete
        const validateNodeCompletion = (node) => {
            if (!node || !node.parameters) return false;

            switch (node.type) {
                case 'trigger':
                    return !!(node.parameters.entity && node.parameters.action);
                case 'conditions':
                    return !!(
                        node.parameters.conditions &&
                        !isEmptyObject(node.parameters.conditions) &&
                        node.parameters.conditions.parameters &&
                        node.parameters.conditions.parameters.length > 0 &&
                        node.parameters.conditions.parameters.every((group) => {
                            if (!group?.parameters?.length) return false;
                            return group.parameters.every((condition) => {
                                if (!condition || isEmptyObject(condition)) return false;
                                if (
                                    condition?.type === 'number_between' ||
                                    condition?.type === 'number_not_between' ||
                                    condition?.type === 'datetime_in_range' ||
                                    condition?.type === 'datetime_not_in_range'
                                ) {
                                    return !!(
                                        condition?.parameters?.variable &&
                                        condition?.parameters?.valueFrom &&
                                        condition?.parameters?.valueTo
                                    );
                                }

                                // Check if condition has a valid value
                                const hasValidValue = () => {
                                    if (CONDITION_TYPE_NO_INPUT.includes(condition.type)) {
                                        return true; // No input required
                                    }

                                    const value = condition?.parameters?.value;
                                    if (!value) return false;

                                    // Handle array values
                                    if (Array.isArray(value)) {
                                        return value.length > 0;
                                    }

                                    // Handle object values (check if it's not empty)
                                    if (typeof value === 'object') {
                                        return Object.keys(value).length > 0;
                                    }

                                    // Handle primitive values (string, number, boolean)
                                    return value !== '' && value !== null && value !== undefined;
                                };

                                return !!(condition?.parameters?.variable && hasValidValue());
                            });
                        })
                    );

                case 'action':
                    return !!(
                        node.parameters?.entity &&
                        node.name &&
                        node.parameters?.values &&
                        node.parameters?.values?.length > 0 &&
                        !node.parameters?.values?.some((value) => {
                            if (!value?.value) return true;
                            if (ACTION_OPERATORS_NO_INPUT.includes(value?.value?.type)) return true;
                            return false;
                        })
                    );
                default:
                    return false;
            }
        };

        const flagIncompleteNodes = (node) => {
            if (!node) return;

            const isComplete = validateNodeCompletion(node);

            // Flag the node as incomplete if it's missing required data
            if (!isComplete) {
                node._isIncomplete = true;
                foundIncomplete = true;
            } else {
                delete node._isIncomplete;
            }

            // Recursively check child nodes
            if (node.next && Array.isArray(node.next)) {
                node.next.forEach((nextNode) => flagIncompleteNodes(nextNode));
            }
        };

        if (state.data?.scene) flagIncompleteNodes(state.data.scene);
        dispatch({ type: 'changeData', data: { scene: state.data.scene } });

        if (foundIncomplete) {
            return;
        }

        if (!state.data?.name) {
            onEditDetails((detailsData = {}) => {
                if (state.data?.enabled && !wasEnabledRef.current) {
                    warningModalRef.current?.open(state.data, (moreData = {}) => {
                        save({
                            ...detailsData,
                            ...moreData,
                        });
                    });
                } else {
                    save(detailsData);
                }
            });
            return;
        } else if (state.data?.enabled && !wasEnabledRef.current) {
            warningModalRef.current?.open(state.data, (detailsData) => {
                save(detailsData);
            });
        } else {
            save();
        }
    }, [onEditDetails, state.data, state.id]);

    const handleRef = useCallback((ref) => {
        modalRef.current = ref;
    }, []);

    const handleWarningModalRef = useCallback((ref) => {
        warningModalRef.current = ref;
    }, []);

    const onEditDetails = useCallback(
        (cb) => {
            modalRef.current?.open(state.data, cb);
        },
        [state.data],
    );

    const onSaveHeaderForm = useCallback((data) => {
        dispatch({ type: 'changeData', data });
        setIsTouched(true);
        return Promise.resolve();
    }, []);

    const onClose = useCallback(() => {
        dispatch({ type: 'close' });
        automationRedirectPath();
        setDiscardOpen(false);
        setIsTouched(false);
    }, [dispatch]);

    const closeCancel = useCallback(() => {
        setDiscardOpen();
    }, []);

    const content = useMemo(() => {
        if (!state.open) return null;

        // default to action if from template or trigger, no selection when editing.
        let defaultNode = state.data?.isFromTemplate
            ? state.data?.scene?.next?.[0]?.next?.[0]
            : undefined;
        if (!defaultNode && state.id) defaultNode = state.data?.scene;

        return (
            <div className="fm-automation-builder__content">
                <AutomationBuilderHeader
                    onClose={onRequestClose}
                    title={state.data?.name}
                    description={state.data?.description}
                    onEditDetails={onEditDetails}
                />
                <AutomationBuilderCanvas
                    data={state.data}
                    onChange={onChange}
                    onEditDetails={onEditDetails}
                    defaultNode={defaultNode}
                />
                <AutomationBuilderFooter onClose={onRequestClose} onSave={onSave} />
                <AutomationBuilderHeaderModalForm onRef={handleRef} onSave={onSaveHeaderForm} />
                <AutomationBuilderWarningModal
                    onRef={handleWarningModalRef}
                    name={state.data?.name}
                />
                <ConfirmModal
                    overlayClassName="fm-automation-builder__modal"
                    isOpen={discardOpen}
                    title={getLiteral('label_discard_unsaved_changes')}
                    confirmText={getLiteral('action_discard_changes')}
                    cancelText={getLiteral('action_cancel')}
                    onConfirm={onClose}
                    onClose={closeCancel}
                >
                    <Text>{getLiteral('label_discard_unsaved_changes_desc')}</Text>
                </ConfirmModal>
            </div>
        );
    }, [
        closeCancel,
        discardOpen,
        handleRef,
        handleWarningModalRef,
        onChange,
        onClose,
        onEditDetails,
        onRequestClose,
        onSave,
        onSaveHeaderForm,
        state.data,
        state.id,
        state.open,
    ]);

    return (
        <Modal
            className="fm-automation-builder"
            overlayClassName="fm-automation-builder__overlay"
            onRequestClose={onRequestClose}
            useHeader={false}
            isOpen={state.open}
        >
            {content}
        </Modal>
    );
});

export default AutomationBuilder;
